import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { ArrowLeft } from 'lucide-react'
import { cn } from '@/lib/utils'
import { revealDelay } from '@/lib/motion'
import { HeroTitle } from '@/components/generals/hero-title'
import { SectionLabel } from '@/components/generals/section-label'

interface PageHeroProps {
    id?: string
    label: string
    lines: ReactNode[]
    intro?: ReactNode
    meta?: ReactNode
    lead?: ReactNode
    actions?: ReactNode
    aside?: ReactNode
    back?: { href: string, label: string }
    tone?: 'cream' | 'charcoal' | 'stone' | 'sand'
    decoration?: ReactNode
    children?: ReactNode
    titleClassName?: string
}

const TONES = {
    cream: { section: 'bg-cream text-charcoal', nav: 'light', label: 'light', muted: 'text-charcoal/85', rule: 'bg-charcoal/20', meta: 'text-charcoal/75' },
    stone: { section: 'bg-stone text-charcoal', nav: 'light', label: 'light', muted: 'text-charcoal/85', rule: 'bg-charcoal/25', meta: 'text-charcoal/80' },
    sand: { section: 'bg-sand text-charcoal', nav: 'light', label: 'light', muted: 'text-charcoal/85', rule: 'bg-charcoal/20', meta: 'text-charcoal/80' },
    charcoal: { section: 'bg-charcoal text-cream', nav: 'dark', label: 'dark', muted: 'text-cream/80', rule: 'bg-cream/15', meta: 'text-cream/65' }
} as const

/*
 * Inner page hero: an eyebrow row whose rule draws in, a one or two line headline on 8 columns whose
 * lines rise in turn, and a 4 column side with `lead`, `intro`, `actions` and `aside` aligned to its
 * baseline. `children` sits below for page specific content.
 */
const PageHero: FC<PageHeroProps> = ({
    id = 'page-hero',
    label,
    lines,
    intro,
    meta,
    lead,
    actions,
    aside,
    back,
    tone = 'cream',
    decoration,
    children,
    titleClassName
}): ReactNode => {
    const style = TONES[tone]

    return (
        <section id={id} aria-labelledby={`${id}-title`} data-nav-theme={style.nav} className={cn('relative overflow-hidden', style.section)}>
            {decoration}

            <div className='relative mx-auto max-w-[1440px] px-5 pb-16 pt-32 md:px-10 md:pb-20 md:pt-40'>
                {back && (
                    <Link
                        href={back.href}
                        className='reveal-fade mb-8 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] opacity-80 transition-opacity hover:opacity-100'
                    >
                        <ArrowLeft aria-hidden='true' className='size-4' />
                        {back.label}
                    </Link>
                )}

                <div className='relative flex flex-wrap items-center justify-between gap-x-6 gap-y-3 pb-5'>
                    <span aria-hidden='true' className={cn('reveal-rule absolute inset-x-0 bottom-0 h-px', style.rule)} style={revealDelay(100)} />
                    <SectionLabel tone={style.label} className='reveal-fade'>{label}</SectionLabel>
                    {meta && (
                        <p className={cn('reveal-fade font-mono text-[11px] uppercase tracking-[0.2em]', style.meta)} style={revealDelay(150)}>{meta}</p>
                    )}
                </div>

                <div className='mt-10 grid gap-8 md:mt-14 lg:grid-cols-12 lg:items-end lg:gap-10'>
                    <HeroTitle
                        id={`${id}-title`}
                        className={cn('type-hero lg:col-span-8', titleClassName)}
                        delay={200}
                        lines={lines}
                    />

                    {(lead || intro || actions || aside) && (
                        <div className={cn('space-y-6 lg:col-span-4', style.muted)}>
                            {lead && <div className='reveal-fade' style={revealDelay(320)}>{lead}</div>}
                            {intro && <div className='reveal-fade max-w-md text-[17px] leading-relaxed' style={revealDelay(400)}>{intro}</div>}
                            {actions && <div className='reveal-fade flex flex-wrap items-center gap-x-7 gap-y-4' style={revealDelay(480)}>{actions}</div>}
                            {aside && <div className='reveal-fade' style={revealDelay(540)}>{aside}</div>}
                        </div>
                    )}
                </div>

                {children}
            </div>
        </section>
    )
}

export default PageHero
